import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import {
	Rubric,
	RubricLevel,
	RubricCreatePayload,
	RubricUpdatePayload,
	RubricLevelCreatePayload,
	RubricLevelUpdatePayload,
	RubricLevelsBySkill,
} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class RubricService {
	private readonly BASE_PATH = 'rubrics';
	private readonly LEVEL_PATH = 'rubricLevels';

	public selectedRubricBs$ = new BehaviorSubject<Rubric | undefined>(undefined);
	public readonly selectedRubric$ = this.selectedRubricBs$.asObservable();

	public selectedRubricLevelBs$ = new BehaviorSubject<RubricLevel | undefined>(undefined);
	public readonly selectedRubricLevel$ = this.selectedRubricLevelBs$.asObservable();

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public rubricsBs$: BehaviorSubject<Rubric[]> = new BehaviorSubject<Rubric[]>([]);
	public rubrics$: Observable<Rubric[]> = this.rubricsBs$.pipe(
		filter(rubrics => rubrics !== undefined && rubrics.length > 0)
	);
	public rubricLevelsBs$: BehaviorSubject<RubricLevel[]> = new BehaviorSubject<RubricLevel[]>([]);
	public rubricLevels$: Observable<RubricLevel[]> = this.rubricLevelsBs$.pipe(
		filter(rubricLevels => rubricLevels !== undefined && rubricLevels.length > 0)
	);

	constructor(private api: ApiService) {
		// Load rubrics on init
		this.getRubrics$().catch(error => {
			console.error('Error fetching rubrics:', error);
			this.rubricsBs$.next([]);
		});
	}

	public selectRubric(rubric: Rubric | undefined): void {
		this.selectedRubricBs$.next(rubric);
	}

	public selectRubricLevel(rubricLevel: RubricLevel | undefined): void {
		this.selectedRubricLevelBs$.next(rubricLevel);
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getRubrics$(): Promise<Rubric[]> {
		return this.withLoading(async () => {
			const rubrics = await firstValueFrom(this.api.get<Rubric[]>(this.BASE_PATH + '/all'));
			this.rubricsBs$.next(rubrics);
			return rubrics;
		});
	}

	public async getRubric$(code: string): Promise<Rubric> {
		return this.withLoading(async () => {
			const rubric = await firstValueFrom(this.api.get<Rubric>(`${this.BASE_PATH}/get-by-code/${code}`));
			return rubric;
		});
	}

	public async getRubricContentLevels$(rubricCode: string, courseUuid: string): Promise<RubricLevelsBySkill[]> {
		return this.withLoading(async () => {
			const contentLevels = await firstValueFrom(
				this.api.get<RubricLevelsBySkill[]>(`${this.BASE_PATH}/${rubricCode}/content-levels/${courseUuid}`)
			);
			return contentLevels;
		});
	}

	public async getRubricByLevelUuid$(rubricLevelUuid: string): Promise<Rubric> {
		return this.withLoading(async () => {
			const rubric = await firstValueFrom(this.api.get<Rubric>(`${this.BASE_PATH}/level/${rubricLevelUuid}`));
			return rubric;
		});
	}

	public async createRubric$(payload: RubricCreatePayload): Promise<Rubric> {
		return this.withLoading(async () => {
			const rubric = await firstValueFrom(this.api.post<Rubric>(this.BASE_PATH, payload));
			this.rubricsBs$.next([...this.rubricsBs$.value, rubric]);
			return rubric;
		});
	}

	public async updateRubric$(code: string, payload: RubricUpdatePayload): Promise<Rubric> {
		return this.withLoading(async () => {
			const updatedRubric = await firstValueFrom(this.api.put<Rubric>(`${this.BASE_PATH}/${code}`, payload));
			const currentRubrics = this.rubricsBs$.value;
			const index = currentRubrics.findIndex(r => r.code === code);
			if (index !== -1) {
				currentRubrics[index] = updatedRubric;
				this.rubricsBs$.next([...currentRubrics]);
			}
			return updatedRubric;
		});
	}

	public async deleteRubric$(code: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete(`${this.BASE_PATH}/${code}`));
			this.rubricsBs$.next(this.rubricsBs$.value.filter(r => r.code !== code));
			if (this.selectedRubricBs$.value?.code === code) {
				this.selectRubric(undefined);
			}
		});
	}

	public async getRubricLevels$(rubricCode: string): Promise<RubricLevel[]> {
		return this.withLoading(async () => {
			const rubricLevels = await firstValueFrom(
				this.api.get<RubricLevel[]>(`${this.LEVEL_PATH}/rubric/${rubricCode}`)
			);
			this.rubricLevelsBs$.next(rubricLevels);
			return rubricLevels;
		});
	}

	public async createRubricLevel$(payload: RubricLevelCreatePayload): Promise<RubricLevel> {
		return this.withLoading(async () => {
			const rubricLevel = await firstValueFrom(this.api.post<RubricLevel>(this.LEVEL_PATH, payload));
			this.rubricLevelsBs$.next([...this.rubricLevelsBs$.value, rubricLevel]);
			return rubricLevel;
		});
	}

	public async updateRubricLevel$(uuid: string, payload: RubricLevelUpdatePayload): Promise<RubricLevel> {
		return this.withLoading(async () => {
			const updatedRubricLevel = await firstValueFrom(
				this.api.put<RubricLevel>(`${this.LEVEL_PATH}/${uuid}`, payload)
			);
			const currentRubricLevels = this.rubricLevelsBs$.value;
			const index = currentRubricLevels.findIndex(rl => rl.uuid === uuid);
			if (index !== -1) {
				currentRubricLevels[index] = updatedRubricLevel;
				this.rubricLevelsBs$.next([...currentRubricLevels]);
			}
			return updatedRubricLevel;
		});
	}

	public async deleteRubricLevel$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete(`${this.LEVEL_PATH}/${uuid}`));
			this.rubricLevelsBs$.next(this.rubricLevelsBs$.value.filter(rl => rl.uuid !== uuid));
		});
	}
}
